// frontend/src/Components/SyncTo/PractoSyncRecorderAnimation.jsx
//
// Branded recording visualisation used in place of the old horizontal level
// meter inside RecordModal. The bars + center circle + rotating rings expand
// with the live mic level (0..1) exposed by useRecording().level.
//
// `isRecording` is intentionally the narrow "actively capturing" state — when
// paused/starting/stopping we pass false so the rings + bars come to rest and
// the modal makes the pause visible.
//
// `useAudioLevel` is included for callers outside SyncTo (e.g. ad-hoc record
// pages) that have a MediaStream but no provider-supplied level. RecordModal
// itself does NOT use it — the SyncTo provider already computes `level`.

import React, { useEffect, useMemo, useRef, useState } from 'react';

const clamp = (value, min = 0, max = 1) => Math.max(min, Math.min(max, Number(value) || 0));

const BAR_PROFILE = [0.3, 0.58, 0.42, 0.78, 0.52, 1, 0.7, 0.44, 0.64, 0.5, 0.36];

export default function PractoSyncRecorderAnimation({
  isRecording = false,
  isActive,                  // rings spin / center breathes — defaults to isRecording for back-compat
  level = 0,
  isSpeaking,
  silenceThreshold = 0.035,
  size = 180,
  className = '',
}) {
  const ringsActive = isActive ?? isRecording;
  const normalisedLevel = clamp(level);
  const activeVoice = Boolean(isRecording && (isSpeaking ?? normalisedLevel > silenceThreshold));
  const visualLevel = activeVoice ? normalisedLevel : 0;

  const bars = useMemo(
    () =>
      BAR_PROFILE.map((base, index) => {
        const height = activeVoice ? 8 + base * (18 + visualLevel * 54) : 7;
        return {
          x: 82 + index * 7.8,
          y: 120 - height / 2,
          height,
        };
      }),
    [activeVoice, visualLevel]
  );

  return (
    <div
      className={`ps-recorder-v2 ${activeVoice ? 'is-speaking' : 'is-silent'} ${
        ringsActive ? 'is-active' : 'is-idle'
      } ${className}`}
      style={{
        width: size,
        height: size,
        '--level': visualLevel,
      }}
      aria-label={activeVoice ? 'Recording voice detected' : isRecording ? 'Recording silence' : 'Recording stopped'}
      role="img"
    >
      {/* Explicit width/height locks the rendered size — relying on CSS alone
          left the SVG free to scale up to the modal width on some browsers.
          The ring group is shifted to (120,120) via an SVG attribute transform
          and the circles drawn at (0,0). This way CSS rotate/scale on the
          rings operates around their local (0,0), so `transform-origin: 0 0`
          is unambiguous and doesn't depend on `transform-box: fill-box` being
          honoured on <g>/<circle> (which is patchy across browsers). */}
      <svg
        width={size}
        height={size}
        viewBox="0 0 240 240"
        preserveAspectRatio="xMidYMid meet"
        aria-hidden="true"
      >
        <g transform="translate(120 120)">
          <g className="ps-ring-expander">
            <circle className="ps-ring ps-ring-grey" cx="0" cy="0" r="96" pathLength="100" />
            <circle className="ps-ring ps-ring-blue" cx="0" cy="0" r="84" pathLength="100" />
            <circle className="ps-ring ps-ring-teal" cx="0" cy="0" r="71" pathLength="100" />
          </g>
        </g>

        <circle className="ps-center-circle" cx="120" cy="120" r="43" />
        <rect className="ps-meter-bg" x="67" y="98" width="106" height="44" rx="18" />
        <circle className="ps-record-dot" cx="58" cy="120" r="4.5" />

        {bars.map((bar, index) => (
          <rect
            key={index}
            className={`ps-bar ps-bar-${index + 1}`}
            x={bar.x}
            y={bar.y}
            width="4.6"
            height={bar.height}
            rx="2.3"
          />
        ))}
      </svg>

      <style>{`
        .ps-recorder-v2 {
          --ps-blue: #12338F;
          --ps-teal: #1FCFD2;
          --ps-grey: #D6DADD;
          --level: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: transparent;
        }

        .ps-recorder-v2 svg {
          display: block;
          overflow: visible;
        }

        /* Rings live inside an SVG g translated to (120, 120), so their local
           origin IS the visual center — transform-origin: 0 0 rotates them
           cleanly without depending on transform-box: fill-box. */
        .ps-ring-expander,
        .ps-ring {
          transform-origin: 0 0;
        }

        /* Centered shapes still use fill-box so scale animations grow around
           their own centers (works fine for <rect> + non-grouped <circle>). */
        .ps-center-circle,
        .ps-record-dot,
        .ps-bar {
          transform-box: fill-box;
          transform-origin: center;
        }

        .ps-ring-expander {
          transform: scale(calc(0.975 + (var(--level) * 0.095)));
          transition: transform 90ms ease-out;
        }

        .ps-ring {
          fill: none;
          stroke-linecap: round;
          stroke-linejoin: round;
          transition: opacity 120ms ease-out, stroke-width 120ms ease-out;
          filter: none;
        }

        .ps-ring-blue {
          stroke: var(--ps-blue);
          stroke-width: calc(7.5px + (var(--level) * 2px));
          stroke-dasharray: 75 25;
          animation: psRotateBlue 4.8s linear infinite;
        }

        .ps-ring-teal {
          stroke: var(--ps-teal);
          stroke-width: calc(8.5px + (var(--level) * 2.5px));
          stroke-dasharray: 74 26;
          animation: psRotateTeal 4.1s linear infinite reverse;
        }

        .ps-ring-grey {
          stroke: var(--ps-grey);
          stroke-width: 6.5px;
          stroke-dasharray: 68 32;
          animation: psRotateGrey 6.4s linear infinite;
        }

        .ps-center-circle {
          fill: rgba(31, 207, 210, 0.055);
          stroke: rgba(31, 207, 210, 0.46);
          stroke-width: 1.4px;
          transform: scale(calc(0.92 + (var(--level) * 0.22)));
          transition: transform 90ms ease-out, opacity 120ms ease-out;
          animation: psCenterBreath 1.35s ease-in-out infinite;
        }

        .ps-meter-bg {
          fill: rgba(255, 255, 255, 0.94);
          stroke: rgba(214, 218, 221, 0.95);
          stroke-width: 1.15px;
        }

        .ps-bar {
          transition: height 70ms ease-out, y 70ms ease-out, opacity 100ms ease-out;
          animation: psBarLift 820ms ease-in-out infinite;
        }

        .ps-bar:nth-of-type(odd) { fill: var(--ps-teal); }
        .ps-bar:nth-of-type(even) { fill: var(--ps-blue); }

        .ps-bar-1 { animation-delay: -0.72s; }
        .ps-bar-2 { animation-delay: -0.56s; }
        .ps-bar-3 { animation-delay: -0.43s; }
        .ps-bar-4 { animation-delay: -0.29s; }
        .ps-bar-5 { animation-delay: -0.15s; }
        .ps-bar-6 { animation-delay: -0.04s; }
        .ps-bar-7 { animation-delay: -0.22s; }
        .ps-bar-8 { animation-delay: -0.36s; }
        .ps-bar-9 { animation-delay: -0.51s; }
        .ps-bar-10 { animation-delay: -0.63s; }
        .ps-bar-11 { animation-delay: -0.47s; }

        .ps-record-dot {
          fill: var(--ps-teal);
          opacity: calc(0.35 + (var(--level) * 0.65));
          transform: scale(calc(0.8 + (var(--level) * 0.55)));
          transition: transform 90ms ease-out, opacity 120ms ease-out;
          animation: psDotPulse 1.05s ease-in-out infinite;
        }

        /* When fully idle (no consent yet), pause everything and dim. */
        .ps-recorder-v2.is-idle .ps-ring,
        .ps-recorder-v2.is-idle .ps-center-circle,
        .ps-recorder-v2.is-idle .ps-record-dot,
        .ps-recorder-v2.is-idle .ps-bar {
          animation-play-state: paused;
        }

        /* Bars react to voice — pause + dim when no voice is detected.
           Rings + center + dot keep going whenever the recorder is "active"
           (consent ticked / armed / recording / paused), giving a calm,
           continuous brand presence even before Start is pressed. */
        .ps-recorder-v2.is-silent .ps-bar {
          animation-play-state: paused;
          opacity: 0.48;
        }

        .ps-recorder-v2.is-idle .ps-bar {
          opacity: 0.48;
        }

        .ps-recorder-v2.is-idle .ps-center-circle {
          opacity: 0.48;
        }

        @keyframes psRotateBlue {
          from { transform: rotate(-14deg); }
          to { transform: rotate(346deg); }
        }

        @keyframes psRotateTeal {
          from { transform: rotate(22deg); }
          to { transform: rotate(382deg); }
        }

        @keyframes psRotateGrey {
          from { transform: rotate(138deg); }
          to { transform: rotate(498deg); }
        }

        @keyframes psCenterBreath {
          0%, 100% { opacity: 0.42; }
          50% { opacity: 0.78; }
        }

        @keyframes psDotPulse {
          0%, 100% { opacity: calc(0.45 + (var(--level) * 0.38)); }
          50% { opacity: 1; }
        }

        @keyframes psBarLift {
          0%, 100% { transform: scaleY(calc(0.96 + (var(--level) * 0.05))); }
          50% { transform: scaleY(calc(1.02 + (var(--level) * 0.16))); }
        }

        @media (prefers-reduced-motion: reduce) {
          .ps-recorder-v2 * {
            animation-duration: 0.001ms !important;
            animation-iteration-count: 1 !important;
          }
        }
      `}</style>
    </div>
  );
}

// Optional helper for callers that have a MediaStream but no externally
// computed level. RecordModal does NOT use this — SyncTo's RecordingProvider
// already supplies a smoothed level — but it's exported for ad-hoc usage.
export function useAudioLevel({
  stream,
  isRecording,
  silenceThreshold = 0.018,
  speakingThreshold = 0.04,
  smoothing = 0.78,
  fftSize = 1024,
} = {}) {
  const [level, setLevel] = useState(0);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const rafRef = useRef(null);
  const smoothedRef = useRef(0);

  useEffect(() => {
    if (!stream || !isRecording || typeof window === 'undefined') {
      setLevel(0);
      setIsSpeaking(false);
      smoothedRef.current = 0;
      return undefined;
    }

    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return undefined;

    const audioContext = new AudioContextClass();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = fftSize;
    analyser.smoothingTimeConstant = 0.65;

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const data = new Uint8Array(analyser.fftSize);
    let cancelled = false;

    const tick = () => {
      if (cancelled) return;

      analyser.getByteTimeDomainData(data);

      let sumSquares = 0;
      for (let i = 0; i < data.length; i += 1) {
        const centered = (data[i] - 128) / 128;
        sumSquares += centered * centered;
      }

      const rms = Math.sqrt(sumSquares / data.length);
      const raw = clamp((rms - silenceThreshold) / (0.32 - silenceThreshold));
      const smoothed = smoothedRef.current * smoothing + raw * (1 - smoothing);
      const nextLevel = smoothed < 0.012 ? 0 : smoothed;

      smoothedRef.current = nextLevel;
      setLevel(nextLevel);
      setIsSpeaking(nextLevel > speakingThreshold);

      rafRef.current = requestAnimationFrame(tick);
    };

    tick();

    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      source.disconnect();
      analyser.disconnect();
      audioContext.close().catch(() => {});
      smoothedRef.current = 0;
      setLevel(0);
      setIsSpeaking(false);
    };
  }, [stream, isRecording, silenceThreshold, speakingThreshold, smoothing, fftSize]);

  return { level, isSpeaking };
}
